/**
 * @file src/pages/authentication-modal/components/LoginForm.jsx
 * @description Login form — email and password with show/hide toggle.
 *
 * What changed from the original:
 *  - Unused watchedFields variable removed
 *  - error?.response?.data?.message extraction removed —
 *    ApiError from lib/axios.js already normalises this into error.message
 *    so the catch block just reads error.message directly
 *  - console.error removed
 *  - Duplicate email validation removed — react-hook-form handles it,
 *    no need to re-validate in onFormSubmit
 *  - aria attributes added for accessibility
 *
 * Rules:
 *  - No API calls, no Redux, no navigation
 *  - onSubmit prop handles all side effects in AuthenticationModal
 */

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import Input from "components/ui/Input";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { EMAIL_REGEX } from "config/constants";

// ---------------------------------------------------------------------------
// LoginForm
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   onSubmit:        (data: { email: string, password: string }) => Promise<void>,
 *   loading?:        boolean,
 *   onSwitchToSignup: () => void,
 *   disabled?:       boolean,
 * }} props
 */
const LoginForm = ({
  onSubmit,
  loading = false,
  onSwitchToSignup,
  disabled = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // ── Submit ────────────────────────────────────────────────────────────────
  const onFormSubmit = async (data) => {
    setSubmitError("");
    try {
      await onSubmit(data);
    } catch (error) {
      // ApiError.message is already normalised in lib/axios.js interceptor
      setSubmitError(
        error?.message ?? "Invalid email or password. Please try again.",
      );
    }
  };

  const isDisabled = disabled || loading;

  return (
    <form
      onSubmit={handleSubmit(onFormSubmit)}
      className="space-y-4"
      noValidate
    >
      {/* ── Email ──────────────────────────────────────────────────────── */}
      <div>
        <Input
          id="login-email"
          type="email"
          placeholder="Work email"
          autoComplete="email"
          disabled={isDisabled}
          aria-describedby={errors.email ? "login-email-error" : undefined}
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: EMAIL_REGEX,
              message: "Please enter a valid email address",
            },
          })}
          className={errors.email ? "border-red-500 focus:ring-red-500" : ""}
        />
        {errors.email && (
          <div
            id="login-email-error"
            role="alert"
            className="mt-1 text-sm text-red-600 flex items-center gap-1"
          >
            <Icon name="AlertCircle" size={16} />
            <span>{errors.email.message}</span>
          </div>
        )}
      </div>

      {/* ── Password ───────────────────────────────────────────────────── */}
      <div>
        <div className="relative">
          <Input
            id="login-password"
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            autoComplete="current-password"
            disabled={isDisabled}
            aria-describedby={
              errors.password ? "login-password-error" : undefined
            }
            {...register("password", {
              required: "Password is required",
              minLength: {
                value: 8,
                message: "Password must be at least 8 characters",
              },
            })}
            className={`pr-10 ${
              errors.password ? "border-red-500 focus:ring-red-500" : ""
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            disabled={isDisabled}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={20} />
          </button>
        </div>
        {errors.password && (
          <div
            id="login-password-error"
            role="alert"
            className="mt-1 text-sm text-red-600 flex items-center gap-1"
          >
            <Icon name="AlertCircle" size={16} />
            <span>{errors.password.message}</span>
          </div>
        )}
      </div>

      {/* ── Submit error banner ────────────────────────────────────────── */}
      {submitError && (
        <div
          role="alert"
          className="text-sm text-red-600 flex items-center gap-1 bg-red-50 p-3 rounded-md border border-red-200"
        >
          <Icon name="AlertCircle" size={16} className="shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      {/* ── Submit button ──────────────────────────────────────────────── */}
      <Button
        type="submit"
        variant="primary"
        fullWidth
        loading={loading}
        disabled={!isValid || isDisabled}
        className="mt-6"
      >
        Sign In
      </Button>

      {/* ── Switch to signup ───────────────────────────────────────────── */}
      <div className="text-center mt-4">
        <button
          type="button"
          onClick={onSwitchToSignup}
          disabled={isDisabled}
          className="text-sm text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50"
        >
          Don't have an account? Sign up
        </button>
      </div>
    </form>
  );
};

export default LoginForm;
